import {
  ApiState,
  PaginatedResponse,
  SortOrder
} from '@/store/types/general.types';

export interface OrderState {
  apiState: {
    getMySoftwareOrders: ApiState;
    getMyCourseOrders: ApiState;
    getMyOrders: ApiState;
    getOrder: ApiState;
    createOrder: ApiState;
    updateOrder: ApiState;
    retryPayment: ApiState;
  };
  authKey: string | undefined;
  orders: Order[] | undefined;
  softwareOrders: PaginatedResponse<Order> | undefined;
  courseOrders: PaginatedResponse<Order> | undefined;
  order: Order | undefined;
}

export interface OrderPayment {
  id: number;
  amount: number;
  currency: string;
  status: string; // Should match PaymentStatusTypes enum from backend
  createdAt: Date;
  updatedAt: Date;
  paymentType: PaymentMethods;
  txnResponsePayload: any;
}

export enum PaymentMethods {
  DIRECT_TRANSFER = 'DIRECT_TRANSFER',
  CREDIT_CARD = 'CREDIT_CARD'
}

export interface OldOrderDTO {
  id: number;
  orderNumber: number;
  createdAt: Date;
  updatedAt: Date;
  userId: number;
  productId: number;
  productName: string;
  paymentId: number;
  paymentAmount: number;
  paymentCurrency: string;
  paymentTxnResponsePayload: BPointV3TxnResp | BPointV5TxnResp;
  paymentStatus: string;
  paymentCreatedAt: Date;
  paymentUpdatedAt: Date;
  gstAmount: number;
  streetAddress: string | null;
  city: string | null;
  country: string | null;
  payments: OrderPayment[];
}

export interface OrderItem {
  id: number;
  name: string;
  description: string;
  // We can add other fields from the backend response if needed later
}

export interface Order {
  id: number; // This is the Order ID
  orderNumber: string;
  createdAt: Date;
  payments: OrderPayment[];
  orderItems: OrderItem[];
}

export interface CreateOrderPayload {
  productId: number;
  billingAddress: BillingAddress | null;
}

export interface CreateOrderResponse {
  paymentRequired: boolean;
  purchasePaymentUrl?: string;
}

export interface BillingAddress {
  streetAddress: string;
  city: string;
  country: string;
}

export interface GetOrdersParams {
  limit: number;
  page: number;
  searchQuery?: string;
  sortColumn?: string;
  sortOrder?: SortOrder;
}

export interface BPointV3TxnResp {
  Action: string;
  Amount: number;
  AmountOriginal: number;
  AmountSurcharge: number;
  ThreeDSResponse: null;
  AuthoriseId: string;
  BankAccountDetails: null;
  BankResponseCode: string;
  CVNResult: BPointV3CVNResult;
  CardDetails: BPointV3CardDetails;
  CardType: string;
  Currency: string;
  MerchantReference: string;
  IsThreeDS: boolean;
  IsCVNPresent: boolean;
  MerchantNumber: string;
  OriginalTxnNumber: null;
  ProcessedDateTime: string;
  RRN: string;
  ReceiptNumber: string;
  Crn1: number;
  Crn2: string;
  Crn3: string;
  ResponseCode: string;
  ResponseText: string;
  BillerCode: null;
  SettlementDate: string;
  Source: string;
  StoreCard: boolean;
  IsTestTxn: boolean;
  SubType: string;
  TxnNumber: string;
  DVToken: null;
  Type: string;
  FraudScreeningResponse: BPointV3FraudScreeningResponse;
  StatementDescriptor: BPointV3StatementDescriptor;
}

export interface BPointV3CVNResult {
  CVNResultCode: string;
}

export interface BPointV3CardDetails {
  CardHolderName: string;
  ExpiryDate: string;
  MaskedCardNumber: string;
  Category: string;
  Issuer: string;
  IssuerCountryCode: string;
  Localisation: string;
  SubType: string;
}

export interface BPointV3FraudScreeningResponse {
  ReDResponse: BPointV3ReDResponse;
  ResponseCode: string;
  ResponseMessage: string;
  TxnRejected: boolean;
}

export interface BPointV3ReDResponse {
  FRAUD_REC_ID: string;
  FRAUD_RSP_CD: string;
  FRAUD_STAT_CD: string;
  ORD_ID: string;
  REQ_ID: string;
  STAT_CD: string;
}

export interface BPointV3StatementDescriptor {
  AddressLine1: string;
  AddressLine2: string;
  City: string;
  CompanyName: string;
  CountryCode: string;
  Postcode: string;
  State: string;
  MerchantName: string;
  PhoneNumber: string;
}

export interface BPointV5TxnResp {
  action: string;
  type: string;
  subType: string;
  source: string;
  billerCode: string;
  merchantNumber: string;
  crn1: number;
  crn2: string;
  crn3: string;
  merchantReference: string;
  currency: string;
  amount: number;
  amountOriginal: number;
  amountSurcharge: number;
  paymentMethod: {
    token: string;
    card: BPointV5CardDetails;
  };
  processedDateTime: string;
  settlementDate: string;
  responseCode: string;
  bankResponseCode: string;
  responseText: string;
  txnNumber: string;
  receiptNumber: string;
  authoriseId: string;
  rrn: string;
  storeCard: boolean;
  cvnResult: {
    code: string;
  };
  isThreeDs: boolean;
  isCvnPresent: boolean;
  isTestTxn: boolean;
}

export interface BPointV5CardDetails {
  number: string;
  expiry: {
    month: string;
    year: string;
  };
  scheme: string;
  localisation: string;
  type: string;
}
